import { Stack } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TAPIKeyScope } from '@feathr/blackbox';
import { Checkbox, Label, Text } from '@feathr/components';

import ScopeGroupRow from './ScopeGroupRow';
import { getAllScopes, getResourceGroups, getTriStateValue } from './ScopePicker.utils';

import * as styles from './ScopePicker.css';

export interface IScopePickerProps {
  selected: Set<TAPIKeyScope>;
  onChange: (next: Set<TAPIKeyScope>) => void;
}

/** The read-only scope with no write counterpart, rendered as a standalone checkbox. */
const FEED_READ: TAPIKeyScope = 'feed:read';

function ScopePicker({ onChange, selected }: Readonly<IScopePickerProps>): JSX.Element {
  const { t } = useTranslation();
  const groups = getResourceGroups();
  const allScopes = getAllScopes();

  const allSelected = allScopes.every((scope) => selected.has(scope));
  const someSelected = allScopes.some((scope) => selected.has(scope));
  const allValue = getTriStateValue(allSelected, someSelected);

  function handleToggleAll(checked?: boolean): void {
    onChange(checked ? new Set<TAPIKeyScope>(allScopes) : new Set<TAPIKeyScope>());
  }

  function handleToggleFeed(checked?: boolean): void {
    const next = new Set<TAPIKeyScope>(selected);
    if (checked) {
      next.add(FEED_READ);
    } else {
      next.delete(FEED_READ);
    }
    onChange(next);
  }

  return (
    <Stack className={styles.root} gap={'sm'}>
      <Label>{t('Scopes')}</Label>
      <Text.SmallMedium>{t('Scopes define the access for personal keys.')}</Text.SmallMedium>

      <Checkbox label={t('All')} onChange={handleToggleAll} value={allValue} />

      {groups.map((group) => (
        <ScopeGroupRow group={group} key={group.resource} onChange={onChange} selected={selected} />
      ))}

      <Checkbox
        label={t('Feed (Read only)')}
        onChange={handleToggleFeed}
        value={selected.has(FEED_READ)}
      />
    </Stack>
  );
}

export default ScopePicker;
